import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { prospectsAPI, leadsAPI, serviceDeliveryAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { MeetingTemplateDialog } from './MeetingTemplateDialog';
import { LeadMeetingsDialog } from './LeadMeetingsDialog';
import {
  Users,
  UserPlus,
  Building,
  Mail,
  Phone,
  MapPin,
  Globe,
  Instagram,
  Search,
  Plus,
  MoreHorizontal,
  Calendar,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  Eye,
  Loader2,
  Target,
  Briefcase,
  RefreshCw,
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Estados para prospectos
const prospectStatusConfig = {
  contactar: { label: 'Contactar', color: 'bg-gray-100 text-gray-700' },
  contactado: { label: 'Contactado', color: 'bg-blue-100 text-blue-700' },
  presupuesto_enviado: { label: 'Presupuesto Enviado', color: 'bg-amber-100 text-amber-700' },
  en_seguimiento: { label: 'En Seguimiento', color: 'bg-purple-100 text-purple-700' },
  es_cliente: { label: 'Es Cliente', color: 'bg-emerald-100 text-emerald-700' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-700' },
  prospecto_antiguo: { label: 'Prospecto antiguo - auditoría enviada', color: 'bg-orange-100 text-orange-700' },
};

// Estados para clientes
const clientStatusConfig = {
  activo: { label: 'Activo', color: 'bg-emerald-100 text-emerald-700' },
  pausado: { label: 'Pausado', color: 'bg-amber-100 text-amber-700' },
  finalizado: { label: 'Finalizado', color: 'bg-gray-100 text-gray-700' },
};

export function CRMPage() {
  const [activeTab, setActiveTab] = useState('prospectos');
  const { user } = useAuthStore();
  const isCeo = user?.role === 'ceo';
  const [meetingsProspect, setMeetingsProspect] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('estado');
  const [prospects, setProspects] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingProspect, setIsAddingProspect] = useState(false);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [refreshingSheet, setRefreshingSheet] = useState(false);
  const [sheetLastSync, setSheetLastSync] = useState(null);
  
  const [newProspect, setNewProspect] = useState({
    contacto: '', mail: '', telefono: '', ubicacion: '', nicho: '', ig: '', web: '', estado: 'contactar'
  });
  
  const [newClient, setNewClient] = useState({
    empresa: '', contacto: '', mail: '', telefono: '', servicio: '', estado: 'activo', direccion: '', fechaInicio: '', notas: '', historial: '', value: 0
  });

  const [editClient, setEditClient] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [logEntries, setLogEntries] = useState([]);
  const [newLog, setNewLog] = useState({ texto: '', fecha: new Date().toISOString().slice(0, 10) });
  const [addingLog, setAddingLog] = useState(false);

  const openEditClient = async (client) => {
    const cid = client._id || client.id;
    // Always load the freshest data from the server so nothing looks stale
    let fresh = client;
    try {
      fresh = await serviceDeliveryAPI.getClient(cid);
    } catch (e) {
      // fall back to the data we already have
    }
    setEditForm({
      nombre: fresh.nombre || client.empresa || '',
      contacto: fresh.contacto || client.contacto || '',
      email: fresh.email || client.mail || '',
      telefono: fresh.telefono || client.telefono || '',
      servicio: fresh.servicio || client.servicio || '',
      estado: fresh.estado || client.estado || 'activo',
      fechaInicio: (fresh.fechaInicio || client.fechaInicio || '').slice(0, 10),
      observaciones: fresh.observaciones || client.notas || '',
    });
    setLogEntries(Array.isArray(fresh.change_log) ? fresh.change_log : []);
    setNewLog({ texto: '', fecha: new Date().toISOString().slice(0, 10) });
    setEditClient({ ...client, _id: fresh._id || cid });
  };

  const handleAddLog = async () => {
    if (!newLog.texto?.trim()) { toast.error('Escribe la anotación'); return; }
    setAddingLog(true);
    try {
      const cid = editClient._id || editClient.id;
      const entry = await serviceDeliveryAPI.addClientLog(cid, { texto: newLog.texto.trim(), fecha: newLog.fecha });
      setLogEntries((prev) => [...prev, entry]);
      setNewLog({ texto: '', fecha: new Date().toISOString().slice(0, 10) });
      toast.success('Anotación añadida');
    } catch (e) {
      toast.error(e?.message || 'Error al añadir la anotación');
    } finally {
      setAddingLog(false);
    }
  };

  const handleDeleteLog = async (entryId) => {
    try {
      const cid = editClient._id || editClient.id;
      await serviceDeliveryAPI.deleteClientLog(cid, entryId);
      setLogEntries((prev) => prev.filter((e) => e.id !== entryId));
    } catch (e) {
      toast.error(e?.message || 'Error al eliminar');
    }
  };

  const handleSaveEditClient = async () => {
    if (!editForm?.nombre?.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSavingEdit(true);
    try {
      const cid = editClient._id || editClient.id;
      // If there's a pending annotation typed but not added yet, save it too
      if (newLog.texto?.trim()) {
        const entry = await serviceDeliveryAPI.addClientLog(cid, { texto: newLog.texto.trim(), fecha: newLog.fecha });
        setLogEntries((prev) => [...prev, entry]);
        setNewLog({ texto: '', fecha: new Date().toISOString().slice(0, 10) });
      }
      await serviceDeliveryAPI.updateClient(cid, editForm);
      toast.success('Cambios guardados correctamente');
      setEditClient(null);
      await loadData();
    } catch (e) {
      toast.error(e?.message || 'Error al guardar los cambios');
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Normalize helpers for dedup (match sheet prospects vs DB prospects)
  const normEmail = (e) => (e || '').toLowerCase().trim();
  const normPhone = (p) => (p || '').replace(/\D/g, '');
  const normName = (n) => (n || '').toLowerCase().trim().replace(/\s+/g, ' ');

  const loadData = async (forceRefreshSheet = false) => {
    setLoading(true);
    try {
      const [prospectsData, clientsData, leadsData, sheetData] = await Promise.all([
        prospectsAPI.getAll().catch(() => []),
        serviceDeliveryAPI.getClients().catch(() => []),
        leadsAPI.getAll().catch(() => []),
        prospectsAPI.getFromSheet(forceRefreshSheet).catch((err) => {
          console.warn('Sheet prospects unavailable:', err);
          return { prospects: [], last_sync: null };
        }),
      ]);

      // Map DB prospects to CRM expected fields
      const mappedDbProspects = (prospectsData || []).map(p => ({
        ...p,
        source: 'db',
        contacto: p.name || p.contacto || '',
        mail: p.email || p.mail || '',
        telefono: p.phone || p.telefono || '',
        ubicacion: p.location || p.ubicacion || '',
        nicho: p.niche || p.nicho || '',
        ig: p.instagram || p.ig || '',
        web: p.website || p.web || '',
        estado: p.status || p.estado || 'contactar',
        notas: p.notes || p.notas || '',
      }));

      // Map sheet prospects (read-only)
      const mappedSheetProspects = ((sheetData && sheetData.prospects) || []).map(p => ({
        ...p,
        source: 'sheet',
        contacto: p.name || '',
        mail: p.email || '',
        telefono: p.phone || '',
        ubicacion: p.location || '',
        nicho: p.niche || '',
        ig: p.instagram || '',
        web: p.website || '',
        estado: p.status || 'contactar',
        notas: p.notes || '',
      }));

      // Dedup: if a sheet prospect matches an existing DB prospect by email/phone/name, skip the sheet one
      const dbKeys = new Set();
      mappedDbProspects.forEach(p => {
        if (p.mail) dbKeys.add(`email:${normEmail(p.mail)}`);
        if (p.telefono) dbKeys.add(`phone:${normPhone(p.telefono)}`);
        if (p.contacto) dbKeys.add(`name:${normName(p.contacto)}`);
      });

      const uniqueSheetProspects = mappedSheetProspects.filter(p => {
        if (p.mail && dbKeys.has(`email:${normEmail(p.mail)}`)) return false;
        if (p.telefono && dbKeys.has(`phone:${normPhone(p.telefono)}`)) return false;
        if (p.contacto && dbKeys.has(`name:${normName(p.contacto)}`)) return false;
        return true;
      });

      const mappedClients = (clientsData || []).map(c => ({
        ...c,
        id: c.id || c._id,
        empresa: c.nombre || c.name || c.empresa || '',
        contacto: c.contacto || c.contact_name || '',
        mail: c.email || c.mail || '',
        telefono: c.telefono || c.phone || '',
        servicio: c.servicio || c.service || '',
        estado: c.estado || (c.status === 'active' ? 'activo' : c.status === 'paused' ? 'pausado' : c.status || 'activo'),
        direccion: c.direccion || c.address || '',
        fechaInicio: c.fechaInicio || c.start_date || '',
        notas: c.observaciones || c.notes || c.notas || '',
        historial: c.historial || c.history || '',
        value: c.value || 0,
      }));

      // DB prospects first, then unique sheet prospects
      setProspects([...mappedDbProspects, ...uniqueSheetProspects]);
      setClients(mappedClients);
      setLeads(leadsData || []);
      setSheetLastSync(sheetData?.last_sync || null);
    } catch (error) {
      console.error('Error loading CRM data:', error);
      toast.error('Error al cargar datos del CRM');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSheet = async () => {
    setRefreshingSheet(true);
    try {
      await loadData(true);
      toast.success('Prospectos del sheet actualizados');
    } catch {
      toast.error('Error al refrescar el sheet');
    } finally {
      setRefreshingSheet(false);
    }
  };

  const handleAddProspect = async () => {
    if (!newProspect.contacto) {
      toast.error('El nombre de contacto es requerido');
      return;
    }
    try {
      // Map Spanish fields to English API fields
      const prospectData = {
        name: newProspect.contacto,
        email: newProspect.mail,
        phone: newProspect.telefono,
        location: newProspect.ubicacion,
        niche: newProspect.nicho,
        instagram: newProspect.ig,
        website: newProspect.web,
        status: newProspect.estado,
      };
      
      await prospectsAPI.create(prospectData);
      await loadData();
      setNewProspect({ contacto: '', mail: '', telefono: '', ubicacion: '', nicho: '', ig: '', web: '', estado: 'contactar' });
      setIsAddingProspect(false);
      toast.success('Prospecto añadido correctamente');
    } catch (error) {
      console.error('Error creating prospect:', error);
      toast.error('Error al añadir prospecto');
    }
  };

  const handleAddClient = async () => {
    if (!newClient.empresa) {
      toast.error('El nombre de empresa es requerido');
      return;
    }
    try {
      // Use service_clients schema (matches Entrega de Servicios)
      const clientData = {
        nombre: newClient.empresa,
        contacto: newClient.contacto,
        email: newClient.mail,
        telefono: newClient.telefono,
        servicio: newClient.servicio,
        estado: newClient.estado,
        fechaInicio: newClient.fechaInicio,
        observaciones: newClient.notas,
        direccion: newClient.direccion || '',
        historial_breve: newClient.historial || '',
        valor_contrato: newClient.value ? Number(newClient.value) : null,
      };

      // Create client in service_clients (the unified collection)
      await serviceDeliveryAPI.createClient(clientData);

      // Also create a lead entry for the pipeline (status: converted)
      await leadsAPI.create({
        name: newClient.empresa,
        email: newClient.mail,
        phone: newClient.telefono,
        company: newClient.empresa,
        status: 'converted',
        value: newClient.value || 0,
        source: 'direct',
        notes: newClient.notas
      });
      
      await loadData();
      setNewClient({ empresa: '', contacto: '', mail: '', telefono: '', servicio: '', estado: 'activo', direccion: '', fechaInicio: '', notas: '', historial: '', value: 0 });
      setIsAddingClient(false);
      toast.success('Cliente añadido correctamente');
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error('Error al añadir cliente');
    }
  };

  const handleUpdateProspectStatus = async (id, newStatus) => {
    const prospect = prospects.find(p => p.id === id);
    const isSheetProspect = prospect?.source === 'sheet';

    try {
      if (isSheetProspect) {
        // Sheet is read-only. Import to DB with the new status so action persists.
        const created = await prospectsAPI.create({
          name: prospect.contacto,
          email: prospect.mail,
          phone: prospect.telefono,
          niche: prospect.nicho,
          instagram: prospect.ig,
          status: newStatus,
          notes: prospect.notas,
        });
        // If converted to client, also create lead entry
        if (newStatus === 'es_cliente') {
          await leadsAPI.create({
            name: prospect.contacto,
            email: prospect.mail,
            phone: prospect.telefono,
            company: prospect.contacto,
            status: 'converted',
            value: 0,
            source: 'prospecting',
            notes: `Convertido desde prospecto (sheet). Nicho: ${prospect.nicho}`
          });
        }
        await loadData();
        toast.success('Prospecto importado del sheet y actualizado');
        return;
      }

      const updated = await prospectsAPI.update(id, { status: newStatus });

      // If converted to client, also update leads
      if (newStatus === 'es_cliente' && prospect) {
        await leadsAPI.create({
          name: prospect.contacto,
          email: prospect.mail,
          phone: prospect.telefono,
          company: prospect.contacto,
          status: 'converted',
          value: 0,
          source: 'prospecting',
          notes: `Convertido desde prospecto. Nicho: ${prospect.nicho}`
        });
      }

      await loadData();
      // Notify if backend auto-created a service_client
      if (updated?._auto_created_service_client) {
        toast.success('¡Convertido a cliente! Ficha creada en Entrega de Servicios');
      } else {
        toast.success('Estado actualizado');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al actualizar estado');
    }
  };

  const handleDeleteProspect = async (id) => {
    const prospect = prospects.find(p => p.id === id);
    if (prospect?.source === 'sheet') {
      toast.info('Este prospecto viene del Google Sheet. Elimínalo desde la hoja de cálculo.');
      return;
    }
    try {
      await prospectsAPI.delete(id);
      await loadData();
      toast.success('Prospecto eliminado');
    } catch (error) {
      toast.error('Error al eliminar prospecto');
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await serviceDeliveryAPI.deleteClient(id);
      await loadData();
      toast.success('Cliente eliminado');
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const filteredProspects = prospects.filter(p => {
    const matchesQuery = (
      p.contacto.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.nicho?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.mail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.notas?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const matchesStatus = !statusFilter || p.estado === statusFilter;
    return matchesQuery && matchesStatus;
  });

  const filteredClients = clients.filter(c =>
    (c.empresa || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.contacto || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.servicio || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats — todos derivados de datos reales en tiempo real
  const POR_CONTACTAR_STATES = ['contactar', 'sin_contactar', 'nuevo', 'por_contactar', 'pending'];
  const prospectStats = {
    total: prospects.length,
    contactar: prospects.filter(p => POR_CONTACTAR_STATES.includes((p.estado || '').toLowerCase())).length,
    enProceso: prospects.filter(p => ['contactado', 'presupuesto_enviado', 'en_seguimiento'].includes(p.estado)).length,
    // Convertidos = clientes que vienen de un prospecto del CRM (con created_from_prospect_id)
    convertidos: clients.filter(c => c.created_from_prospect_id).length,
  };

  const clientStats = {
    total: clients.length,
    activos: clients.filter(c => c.estado === 'activo').length,
    pausados: clients.filter(c => c.estado === 'pausado').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="crm-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">CRM</h1>
          <p className="text-muted-foreground mt-1">Gestión de prospectos y clientes</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Prospectos</p>
                <p className="text-2xl font-bold">{prospectStats.total}</p>
              </div>
              <Target className="w-8 h-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Por Contactar</p>
                <p className="text-2xl font-bold">{prospectStats.contactar}</p>
              </div>
              <Phone className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Clientes Activos</p>
                <p className="text-2xl font-bold">{clientStats.activos}</p>
              </div>
              <Users className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Convertidos</p>
                <p className="text-2xl font-bold">{prospectStats.convertidos}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="prospectos" data-testid="tab-prospectos">
              <Target className="w-4 h-4 mr-2" />
              Prospectos
            </TabsTrigger>
            <TabsTrigger value="clientes" data-testid="tab-clientes">
              <Briefcase className="w-4 h-4 mr-2" />
              Clientes
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            
            {activeTab === 'prospectos' ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleRefreshSheet}
                  disabled={refreshingSheet}
                  data-testid="refresh-prospects-sheet-btn"
                  title={sheetLastSync ? `Última sync: ${new Date(sheetLastSync).toLocaleString('es-ES')}` : 'Sincronizar prospectos del Google Sheet'}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshingSheet ? 'animate-spin' : ''}`} />
                  Sync Sheet
                </Button>
                {isCeo && <MeetingTemplateDialog />}
                <Dialog open={isAddingProspect} onOpenChange={setIsAddingProspect}>
                <DialogTrigger asChild>
                  <Button className="gradient-primary text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Prospecto
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Añadir Prospecto</DialogTitle>
                    <DialogDescription>Completa la información del nuevo prospecto</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>Contacto *</Label>
                      <Input value={newProspect.contacto} onChange={(e) => setNewProspect({...newProspect, contacto: e.target.value})} placeholder="Nombre del contacto" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={newProspect.mail} onChange={(e) => setNewProspect({...newProspect, mail: e.target.value})} placeholder="email@ejemplo.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono</Label>
                      <Input value={newProspect.telefono} onChange={(e) => setNewProspect({...newProspect, telefono: e.target.value})} placeholder="+34 600 000 000" />
                    </div>
                    <div className="space-y-2">
                      <Label>Ubicación</Label>
                      <Input value={newProspect.ubicacion} onChange={(e) => setNewProspect({...newProspect, ubicacion: e.target.value})} placeholder="Ciudad" />
                    </div>
                    <div className="space-y-2">
                      <Label>Nicho</Label>
                      <Input value={newProspect.nicho} onChange={(e) => setNewProspect({...newProspect, nicho: e.target.value})} placeholder="Sector/Industria" />
                    </div>
                    <div className="space-y-2">
                      <Label>Instagram</Label>
                      <Input value={newProspect.ig} onChange={(e) => setNewProspect({...newProspect, ig: e.target.value})} placeholder="@usuario" />
                    </div>
                    <div className="space-y-2">
                      <Label>Web</Label>
                      <Input value={newProspect.web} onChange={(e) => setNewProspect({...newProspect, web: e.target.value})} placeholder="www.ejemplo.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Select value={newProspect.estado} onValueChange={(v) => setNewProspect({...newProspect, estado: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(prospectStatusConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <Button variant="outline" onClick={() => setIsAddingProspect(false)}>Cancelar</Button>
                    <Button onClick={handleAddProspect}>Guardar Prospecto</Button>
                  </div>
                </DialogContent>
              </Dialog>
              </>
            ) : (
              <Dialog open={isAddingClient} onOpenChange={setIsAddingClient}>
                <DialogTrigger asChild>
                  <Button className="gradient-primary text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Cliente
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Añadir Cliente</DialogTitle>
                    <DialogDescription>Completa la información del nuevo cliente</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>Nombre/Empresa *</Label>
                      <Input value={newClient.empresa} onChange={(e) => setNewClient({...newClient, empresa: e.target.value})} placeholder="Nombre de la empresa" />
                    </div>
                    <div className="space-y-2">
                      <Label>Persona de Contacto</Label>
                      <Input value={newClient.contacto} onChange={(e) => setNewClient({...newClient, contacto: e.target.value})} placeholder="Nombre del contacto" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={newClient.mail} onChange={(e) => setNewClient({...newClient, mail: e.target.value})} placeholder="email@ejemplo.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono</Label>
                      <Input value={newClient.telefono} onChange={(e) => setNewClient({...newClient, telefono: e.target.value})} placeholder="+34 600 000 000" />
                    </div>
                    <div className="space-y-2">
                      <Label>Servicio Contratado</Label>
                      <select value={newClient.servicio} onChange={(e) => setNewClient({...newClient, servicio: e.target.value})}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="crm-new-client-servicio">
                        <option value="">Seleccionar servicio...</option>
                        {['Redes Sociales', 'Redes Sociales + Ads', 'Ads', 'Marketing Digital', 'Asesoría', 'Otros'].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Select value={newClient.estado} onValueChange={(v) => setNewClient({...newClient, estado: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(clientStatusConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Dirección</Label>
                      <Input value={newClient.direccion} onChange={(e) => setNewClient({...newClient, direccion: e.target.value})} placeholder="Dirección completa" />
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha de Inicio</Label>
                      <Input type="date" value={newClient.fechaInicio} onChange={(e) => setNewClient({...newClient, fechaInicio: e.target.value})} />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Notas Importantes</Label>
                      <Textarea value={newClient.notas} onChange={(e) => setNewClient({...newClient, notas: e.target.value})} placeholder="Notas relevantes sobre el cliente..." rows={2} />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Historial Breve</Label>
                      <Textarea value={newClient.historial} onChange={(e) => setNewClient({...newClient, historial: e.target.value})} placeholder="Historial de la relación..." rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Valor del Contrato (€)</Label>
                      <Input type="number" value={newClient.value} onChange={(e) => setNewClient({...newClient, value: parseFloat(e.target.value) || 0})} placeholder="0" />
                      <p className="text-xs text-muted-foreground">Se guarda en su ficha y suma en «contratado» en el pipeline del Dashboard CEO</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-6">
                    <Button variant="outline" onClick={() => setIsAddingClient(false)}>Cancelar</Button>
                    <Button onClick={handleAddClient}>Guardar Cliente</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Prospectos Tab */}
        <TabsContent value="prospectos">
          <div className="mb-4 flex items-center gap-2">
            <Select value={statusFilter || 'all'} onValueChange={(v) => setSearchParams(v === 'all' ? {} : { estado: v })}>
              <SelectTrigger className="w-[320px] h-9 text-sm" data-testid="prospect-status-filter">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="prospect-status-filter-all">Todos los estados</SelectItem>
                {Object.entries(prospectStatusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key} data-testid={`prospect-status-filter-${key}`}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {statusFilter && (
            <div className="mb-4 flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="active-status-filter">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-emerald-700">Mostrando solo prospectos en estado:</span>
                <Badge className="bg-emerald-500 text-white">{prospectStatusConfig[statusFilter]?.label || statusFilter}</Badge>
                <span className="text-muted-foreground">({filteredProspects.length} resultados)</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchParams({})}
                className="text-emerald-700 hover:bg-emerald-100"
                data-testid="clear-status-filter-btn"
              >
                Quitar filtro ✕
              </Button>
            </div>
          )}
          <motion.div variants={itemVariants}>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left py-3 px-4 text-sm font-medium">Contacto</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Teléfono</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Ubicación</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Nicho</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">IG</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Web</th>
                        <th className="text-left py-3 px-4 text-sm font-medium">Estado</th>
                        <th className="text-left py-3 px-4 text-sm font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProspects.map((prospect) => (
                        <tr key={prospect.id} className="border-b hover:bg-muted/20" data-testid={`prospect-row-${prospect.id}`}>
                          <td className="py-3 px-4 font-medium">
                            <div className="flex items-center gap-2">
                              <span>{prospect.contacto}</span>
                              {prospect.source === 'sheet' && (
                                <Badge variant="outline" className="text-[10px] border-emerald-400 text-emerald-700 bg-emerald-50" title="Proviene del Google Sheet">
                                  Sheet
                                </Badge>
                              )}
                            </div>
                            {prospect.source === 'sheet' && prospect.notas && (
                              <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 max-w-xs">{prospect.notas}</p>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {prospect.mail && (
                              <a href={`mailto:${prospect.mail}`} className="text-blue-600 hover:underline flex items-center gap-1">
                                <Mail className="w-3 h-3" />{prospect.mail}
                              </a>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {prospect.telefono && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />{prospect.telefono}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {prospect.ubicacion && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />{prospect.ubicacion}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">{prospect.nicho}</td>
                          <td className="py-3 px-4 text-sm">
                            {prospect.ig && (
                              <a href={`https://instagram.com/${prospect.ig.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline flex items-center gap-1">
                                <Instagram className="w-3 h-3" />{prospect.ig}
                              </a>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {prospect.web && (
                              <a href={`https://${prospect.web}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                <Globe className="w-3 h-3" />{prospect.web}
                              </a>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <Select value={prospect.estado} onValueChange={(v) => handleUpdateProspectStatus(prospect.id, v)}>
                              <SelectTrigger className={`w-[160px] h-8 text-xs ${prospectStatusConfig[prospect.estado]?.color}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(prospectStatusConfig).map(([key, config]) => (
                                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-3 px-4">
                            {isCeo && (
                              <Button variant="outline" size="sm" className="h-8" onClick={() => setMeetingsProspect(prospect)} data-testid={`prospect-meetings-btn-${prospect.id}`}>
                                <Calendar className="w-4 h-4 mr-1" />Reuniones
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredProspects.length === 0 && (
                    <div className="text-center py-12">
                      <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">No se encontraron prospectos</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Clientes Tab */}
        <TabsContent value="clientes">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredClients.map((client) => (
              <Card key={client.id} className="card-hover">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Building className="w-5 h-5 text-primary" />
                        {client.empresa}
                      </CardTitle>
                      <CardDescription>{client.contacto}</CardDescription>
                    </div>
                    <Badge className={clientStatusConfig[client.estado]?.color || 'bg-gray-100 text-gray-700'}>
                      {clientStatusConfig[client.estado]?.label || client.estado}
                    </Badge>
                  </div>
                </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {client.mail && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <a href={`mailto:${client.mail}`} className="text-blue-600 hover:underline truncate">{client.mail}</a>
                        </div>
                      )}
                      {client.telefono && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span>{client.telefono}</span>
                        </div>
                      )}
                    </div>
                    
                    {client.servicio && (
                      <div className="p-2 rounded-lg bg-primary/5 text-sm">
                        <span className="font-medium">Servicio:</span> {client.servicio}
                      </div>
                    )}
                    
                    {client.direccion && (
                      <div className="flex items-start gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4 mt-0.5" />
                        <span>{client.direccion}</span>
                      </div>
                    )}
                    
                    {client.fechaInicio && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>Inicio: {new Date(client.fechaInicio).toLocaleDateString('es-ES')}</span>
                      </div>
                    )}
                    
                    {client.notas && (
                      <div className="p-2 rounded-lg bg-amber-50 border border-amber-100 text-sm">
                        <span className="font-medium text-amber-700">Notas:</span>
                        <p className="text-amber-800 mt-1">{client.notas}</p>
                      </div>
                    )}
                    
                    {client.historial && (
                      <div className="p-2 rounded-lg bg-muted/30 text-sm">
                        <span className="font-medium">Historial:</span>
                        <p className="text-muted-foreground mt-1">{client.historial}</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditClient(client)} data-testid={`crm-client-detail-${client.id}`}>
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Detalle
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredClients.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">No se encontraron clientes</p>
                </div>
              )}
            </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editClient} onOpenChange={(open) => !open && setEditClient(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crm-client-edit-dialog">
          <DialogHeader>
            <DialogTitle>Detalle del cliente</DialogTitle>
            <DialogDescription>Modifica los datos y guarda. Los cambios se sincronizan con la ficha de Entrega de Servicios.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div>
                <Label>Nombre / Empresa *</Label>
                <Input value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} data-testid="crm-edit-nombre" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Persona de contacto</Label>
                  <Input value={editForm.contacto} onChange={(e) => setEditForm({ ...editForm, contacto: e.target.value })} data-testid="crm-edit-contacto" />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input value={editForm.telefono} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} data-testid="crm-edit-telefono" />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="crm-edit-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Servicio</Label>
                  <Input value={editForm.servicio} onChange={(e) => setEditForm({ ...editForm, servicio: e.target.value })} data-testid="crm-edit-servicio" />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={editForm.estado} onValueChange={(v) => setEditForm({ ...editForm, estado: v })}>
                    <SelectTrigger data-testid="crm-edit-estado"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(clientStatusConfig).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Fecha de inicio</Label>
                <Input type="date" value={editForm.fechaInicio} onChange={(e) => setEditForm({ ...editForm, fechaInicio: e.target.value })} data-testid="crm-edit-fecha" />
              </div>
              <div>
                <Label>Observaciones</Label>
                <Textarea rows={3} value={editForm.observaciones} onChange={(e) => setEditForm({ ...editForm, observaciones: e.target.value })} data-testid="crm-edit-observaciones" />
              </div>

              <div className="border-t pt-3">
                <Label className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Historial de cambios</Label>
                <p className="text-[11px] text-muted-foreground mb-2">Anota cambios con su fecha (ej. servicios contratados, meses sin Ads, etc.).</p>
                <div className="space-y-2 max-h-44 overflow-y-auto mb-2" data-testid="crm-log-list">
                  {logEntries.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">Sin anotaciones todavía.</p>
                  )}
                  {[...logEntries].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')).map((entry) => (
                    <div key={entry.id} className="flex items-start gap-2 p-2 rounded-lg bg-muted/40 text-sm group">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold text-primary">{entry.fecha ? new Date(entry.fecha).toLocaleDateString('es-ES') : ''}</span>
                        <p className="text-foreground break-words">{entry.texto}</p>
                        {entry.autor && <span className="text-[10px] text-muted-foreground">— {entry.autor}</span>}
                      </div>
                      <button onClick={() => handleDeleteLog(entry.id)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 shrink-0" title="Eliminar anotación" data-testid={`crm-log-delete-${entry.id}`}>
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-2">
                  <Input type="date" className="w-36 shrink-0" value={newLog.fecha} onChange={(e) => setNewLog({ ...newLog, fecha: e.target.value })} data-testid="crm-log-date" />
                  <Textarea rows={1} className="flex-1 min-h-[40px]" placeholder="Nueva anotación..." value={newLog.texto} onChange={(e) => setNewLog({ ...newLog, texto: e.target.value })} data-testid="crm-log-text" />
                  <Button type="button" variant="secondary" onClick={handleAddLog} disabled={addingLog} data-testid="crm-log-add" className="shrink-0">
                    {addingLog ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    Añadir
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditClient(null)} disabled={savingEdit}>Cancelar</Button>
                <Button onClick={handleSaveEditClient} disabled={savingEdit} data-testid="crm-edit-save">
                  {savingEdit ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Guardar cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {isCeo && <LeadMeetingsDialog prospect={meetingsProspect} onClose={() => setMeetingsProspect(null)} />}
    </motion.div>
  );
}
